// ============================================================================
// CHANGO V5.0 — Ops 套餐管理页 (§9.5.1)
// ============================================================================
// /ops/workspaces/:id/plan
//
// 布局 (4 区块):
//   (a) 顶部状态条: 当前套餐编号 + 中文名 + enabled_layers + 颜色标识
//                   (A 系蓝 / B 系绿 / 加购橙)
//   (b) 9 套餐双栏选择器: 左 A 系蓝底, 右 B 系绿底; 当前 ●, 其他 ○
//   (c) 变更预览: 模块差异 / 价差 / 工作区数 / 解锁功能
//   (d) 二次确认 modal: 公司名 + 负责人 + 套餐变化, 肉眼核对即可
//
// 接口:
//   GET  /api/ops/workspaces/:id/plan       拉数据
//   POST /api/ops/workspaces/:id/plan       提交变更 (走 §19.3.2 三层校验之后端层)
// ============================================================================

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import {
  brandForSku, A_SERIES, B_SERIES, SERIES_COLORS, SKU_BRAND_MAP
} from '../../lib/skuBrandVersion.js';

function fmtYuan(v) {
  if (v == null) return '未填';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function PackageCard({ skuCode, current, selected, onSelect, prices }) {
  const info = brandForSku(skuCode);
  const colors = SERIES_COLORS[info.series] || SERIES_COLORS['_'];
  const isCurrent = current === skuCode;
  const isSelected = selected === skuCode;
  const price = prices?.[skuCode]?.amount_rmb;
  const original = prices?.[skuCode]?.original_amount_rmb;
  const hasPromo = original != null && Number(original) > Number(price ?? 0);
  return (
    <button
      type="button"
      onClick={() => onSelect(skuCode)}
      className={[
        'w-full text-left rounded-lg border p-3 transition-colors',
        isSelected ? `${colors.card} ring-2 ring-offset-1 ring-current` : 'border-slate-200 bg-white hover:bg-slate-50',
        isCurrent ? 'shadow-sm' : ''
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="text-base">
            {isSelected ? '●' : '○'}
          </span>
          <span className="font-semibold text-slate-800">{info.shortName}</span>
        </div>
        {isCurrent && (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${colors.badge}`}>
            当前
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] text-slate-500">
        {info.layers.join(' + ') || '—'} · {info.hasELayer ? '含运营主体层' : '单主体'}
      </div>
      <div className="mt-2 text-sm">
        {hasPromo && (
          <div className="text-[11px] text-slate-400 line-through">{fmtYuan(original)}</div>
        )}
        <div className={hasPromo ? 'text-rose-600 font-semibold' : 'text-slate-900 font-semibold'}>
          {fmtYuan(price)}
          {hasPromo && (
            <span className="ml-1 inline-block rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">
              限时特价
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function ChangePreview({ from, to, prices }) {
  const fromInfo = brandForSku(from);
  const toInfo = brandForSku(to);
  const fromLayers = new Set(fromInfo.layers);
  const toLayers = new Set(toInfo.layers);
  const added = [...toLayers].filter((x) => !fromLayers.has(x));
  const removed = [...fromLayers].filter((x) => !toLayers.has(x));
  const fromPrice = Number(prices?.[from]?.amount_rmb ?? 0);
  const toPrice = Number(prices?.[to]?.amount_rmb ?? 0);
  const diff = toPrice - fromPrice;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <h3 className="text-sm font-semibold text-slate-800">变更预览</h3>
      <div className="mt-2 space-y-1 text-sm text-slate-700">
        <div>
          <span className="text-slate-500">套餐：</span>
          <span className="font-mono text-xs">{fromInfo.shortName}</span>
          <span className="mx-2 text-slate-400">→</span>
          <span className="font-mono text-xs font-semibold">{toInfo.shortName}</span>
        </div>
        <div>
          <span className="text-slate-500">模块变化：</span>
          {added.length > 0 && <span className="text-emerald-600">+ {added.join(' / ')}</span>}
          {added.length > 0 && removed.length > 0 && <span className="mx-1">·</span>}
          {removed.length > 0 && <span className="text-rose-600">− {removed.join(' / ')}</span>}
          {added.length === 0 && removed.length === 0 && <span className="text-slate-400">无</span>}
        </div>
        <div>
          <span className="text-slate-500">价格变化：</span>
          {fmtYuan(fromPrice)} → {fmtYuan(toPrice)}
          {diff !== 0 && (
            <span className={diff > 0 ? 'ml-2 text-rose-600' : 'ml-2 text-emerald-600'}>
              （{diff > 0 ? '+' : ''}{fmtYuan(Math.abs(diff))}）
            </span>
          )}
        </div>
        <div>
          <span className="text-slate-500">运营主体层：</span>
          {fromInfo.hasELayer ? '有' : '无'} → {toInfo.hasELayer ? '有' : '无'}
          {fromInfo.hasELayer !== toInfo.hasELayer && (
            <span className="ml-1 text-slate-400">（变更）</span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          数据保留：老报价 / 客户 / 产品 / BOM / 工序数据一行不动
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ open, plan, onCancel, onConfirm, busy }) {
  if (!open || !plan) return null;
  const fromInfo = brandForSku(plan.fromCode);
  const toInfo = brandForSku(plan.toCode);
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/70" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-base font-semibold text-slate-800">请核实变更信息</h3>
        <hr className="my-3" />
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">公司</dt>
            <dd className="text-right font-medium text-slate-900">{plan.companyName}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">负责人</dt>
            <dd className="text-right">
              <div className="text-sm font-medium text-slate-900">{plan.ownerLabel}</div>
              <div className="text-xs font-mono text-slate-500">{plan.ownerEmail}</div>
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">当前</dt>
            <dd className="text-right">
              <div className="font-medium">{fromInfo.shortName}</div>
              <div className="text-xs text-slate-500">{fromInfo.layers.join(' + ') || '—'}</div>
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">变更为</dt>
            <dd className="text-right">
              <div className="font-semibold text-blue-700">{toInfo.shortName}</div>
              <div className="text-xs text-blue-500">{toInfo.layers.join(' + ') || '—'}</div>
            </dd>
          </div>
        </dl>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? '提交中…' : '确认变更'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OpsWorkspacePlan() {
  const { id } = useParams();
  const [data, setData] = useState(null);    // { workspace, current_plan }
  const [prices, setPrices] = useState({});  // { [sku_code]: { amount_rmb, original_amount_rmb } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // 用户选中的新 sku_code
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [planRes, pricesRes] = await Promise.all([
        api.get(`/api/ops/workspaces/${encodeURIComponent(id)}/plan`),
        api.get('/api/ops/sku-pricing')
      ]);
      setData(planRes);
      const priceMap = {};
      const items = Array.isArray(pricesRes?.items) ? pricesRes.items : [];
      for (const r of items) {
        priceMap[r.sku_code] = {
          amount_rmb: r.amount_rmb,
          original_amount_rmb: r.original_amount_rmb
        };
      }
      setPrices(priceMap);
      setSelected(planRes?.current_plan?.sku_code ?? null);
    } catch (err) {
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!selected || !data) return;
    setBusy(true);
    try {
      const newSku = SKU_BRAND_MAP[selected];
      if (!newSku) throw new Error('未知 SKU');
      const res = await api.post(`/api/ops/workspaces/${encodeURIComponent(id)}/plan`, {
        enabled_layers: newSku.layers
      });
      setOkMsg(`已变更为 ${res?.sku_name || newSku.shortName}`);
      setConfirmOpen(false);
      await load();
      setTimeout(() => setOkMsg(null), 6000);
    } catch (err) {
      setError(err.message || '变更失败');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">加载中…</div>;
  if (error && !data) return <div className="p-8 text-rose-600">{error}</div>;
  if (!data) return <div className="p-8 text-slate-500">未找到该 workspace</div>;

  const ws = data.workspace;
  const cur = data.current_plan;
  const curInfo = brandForSku(cur.sku_code);
  const curColors = SERIES_COLORS[curInfo.series] || SERIES_COLORS['_'];
  const isChanged = selected && selected !== cur.sku_code;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl px-6 py-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <Link to="/ops/customers" className="text-xs text-slate-500 hover:text-slate-700">← 返回客户列表</Link>
            <h1 className="mt-1 text-xl font-semibold text-slate-800">工作区套餐管理</h1>
          </div>
          <button type="button" onClick={load} className="rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-100">
            刷新
          </button>
        </div>

        {/* (a) 顶部状态条 */}
        <div className={`mb-4 rounded-lg border p-4 ${curColors.card}`}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xs text-slate-500">{ws.company_name}</div>
              <div className="mt-0.5 text-base font-semibold text-slate-800">{curInfo.shortName}</div>
              <div className="mt-0.5 text-xs text-slate-500">
                负责人：{ws.owner_display_name || ws.owner_name || ws.owner_email || '—'} · 模块：{cur.enabled_layers.join(' + ') || '—'}
              </div>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${curColors.badge}`}>
              当前 {curInfo.series === 'A' ? 'A 系（含工厂底盘）' : curInfo.series === 'B' ? 'B 系（仅外贸底盘）' : '加购'}
            </span>
          </div>
        </div>

        {okMsg && (
          <div className="mb-4 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {okMsg}
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {error}
          </div>
        )}

        {/* (b) 9 套餐双栏选择器 */}
        <div className="grid gap-4 md:grid-cols-2">
          <section className="rounded-lg border-2 border-blue-300 bg-blue-50/50 p-3">
            <h2 className="mb-3 text-sm font-semibold text-blue-800">A 系（含工厂模块 A）</h2>
            <div className="space-y-2">
              {A_SERIES.map((code) => (
                <PackageCard
                  key={code}
                  skuCode={code}
                  current={cur.sku_code}
                  selected={selected}
                  onSelect={setSelected}
                  prices={prices}
                />
              ))}
            </div>
          </section>
          <section className="rounded-lg border-2 border-emerald-300 bg-emerald-50/50 p-3">
            <h2 className="mb-3 text-sm font-semibold text-emerald-800">B 系（仅外贸模块 B）</h2>
            <div className="space-y-2">
              {B_SERIES.map((code) => (
                <PackageCard
                  key={code}
                  skuCode={code}
                  current={cur.sku_code}
                  selected={selected}
                  onSelect={setSelected}
                  prices={prices}
                />
              ))}
            </div>
          </section>
        </div>

        {/* (c) 变更预览 */}
        {isChanged && (
          <div className="mt-4">
            <ChangePreview from={cur.sku_code} to={selected} prices={prices} />
          </div>
        )}

        {/* 保存按钮 */}
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">
            提交后 60 秒内客户浏览器会自动检测并刷新页面（§9.5.1）
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelected(cur.sku_code)}
              disabled={!isChanged}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!isChanged}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              保存变更
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen && isChanged}
        plan={isChanged ? {
          companyName: ws.company_name,
          ownerLabel: ws.owner_display_name || ws.owner_name || ws.owner_email || '—',
          ownerEmail: ws.owner_email,
          fromCode: cur.sku_code,
          toCode: selected
        } : null}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={submit}
        busy={busy}
      />
    </div>
  );
}
